/**
 * Toast component — renderer for the global toast queue (lib/toast.ts).
 *
 * Mounts ONCE in AppLayout via <ToastViewport />. Subscribes to the
 * module-level toast store via useToasts() hook and renders each item
 * as a Radix Toast.Root.
 *
 * iOS 26 Liquid Glass aesthetic:
 *   - rounded-2xl bg-white/95 backdrop-blur-xl
 *   - ring-1 ring-zinc-900/[0.06] shadow-2xl
 *   - tone-specific accent on left edge (success=emerald, error=red, info=brand)
 *   - slide-in from right with subtle fade
 */

import * as Toast from "@radix-ui/react-toast";
import { CheckCircle2, AlertCircle, Info, X } from "lucide-react";

import { useToasts, dismiss, type ToastTone, type ToastItem } from "@/lib/toast";
import { cn } from "@/lib/cn";

// --- Tone styling ---

const toneStyles: Record<ToastTone, { icon: string; accent: string }> = {
  success: {
    icon: "text-emerald-500",
    accent: "before:bg-emerald-500",
  },
  error: {
    icon: "text-red-500",
    accent: "before:bg-red-500",
  },
  info: {
    icon: "text-brand-500",
    accent: "before:bg-brand-500",
  },
};

const toneIcons: Record<ToastTone, React.ReactNode> = {
  success: <CheckCircle2 className="h-5 w-5" aria-hidden="true" />,
  error: <AlertCircle className="h-5 w-5" aria-hidden="true" />,
  info: <Info className="h-5 w-5" aria-hidden="true" />,
};

// --- Renderer (mount in AppLayout) ---

export function ToastViewport() {
  const items = useToasts();

  // duration=Infinity: Radix special-cases Infinity (schedules NO setTimeout).
  // The previous Number.MAX_SAFE_INTEGER is a FINITE number above setTimeout's
  // int32 ceiling (2147483647ms); browsers clamp/overflow it to ~0, so Radix
  // auto-closed every toast in <300ms — mid enter-animation, at opacity ~0.1,
  // before it was ever visible. Dismissal is owned by our store (push schedules
  // setTimeout(dismiss, duration)); Radix must never auto-close.
  return (
    <Toast.Provider swipeDirection="right" duration={Infinity}>
      {items.map((item) => (
        <ToastItemRow key={item.id} item={item} />
      ))}
      <Toast.Viewport
        className={cn(
          `fixed bottom-4 right-4 z-[60] flex w-[calc(100vw-2rem)] max-w-sm`,
          `flex-col gap-2 outline-none`,
        )}
      />
    </Toast.Provider>
  );
}

// --- Single toast row ---

function ToastItemRow({ item }: { item: ToastItem }) {
  const tone = toneStyles[item.tone];

  return (
    <Toast.Root
      open={true}
      onOpenChange={(o) => {
        if (!o) dismiss(item.id);
      }}
      className={cn(
        // Glass panel
        `relative overflow-hidden rounded-2xl bg-white/95 backdrop-blur-xl`,
        `ring-1 ring-zinc-900/[0.06] shadow-2xl`,
        // Left accent bar via ::before
        `before:absolute before:left-0 before:top-0 before:bottom-0 before:w-1`,
        tone.accent,
        // Layout
        `flex items-start gap-3 px-4 py-3 pr-10`,
        // Animation — uses the LOCAL 'slide-up' keyframe (tailwind.config.ts),
        // NOT tailwindcss-animate (not installed; plugins:[]). The plugin's
        // animate-in/slide-in-from-right-full classes never generated, leaving
        // the toast stuck in its invisible initial state. Local keyframe = no
        // extra dependency, guaranteed visible.
        `animate-slide-up`,
      )}
    >
      <div className={cn("mt-0.5 flex-shrink-0", tone.icon)}>
        {toneIcons[item.tone]}
      </div>
      <div className="flex-1 min-w-0">
        <Toast.Title className="font-semibold text-ink">
          {item.title}
        </Toast.Title>
        {item.description ? (
          <Toast.Description className="mt-0.5 text-caption text-ink-muted">
            {item.description}
          </Toast.Description>
        ) : null}
      </div>
      <Toast.Close
        className={cn(
          `absolute right-2 top-2 rounded-full p-1 text-ink-muted`,
          `hover:bg-zinc-900/[0.06] hover:text-ink transition-colors`,
          `focus:outline-none focus-visible:shadow-focus`,
        )}
        aria-label="Dismiss"
      >
        <X className="h-3.5 w-3.5" aria-hidden="true" />
      </Toast.Close>
    </Toast.Root>
  );
}
